import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Signature capture — supports mouse, touch and pen. Emits a PNG data URL
 * that the caller sends to POST /api/v1/agreements/therapist-sign.
 *
 * The pad is deliberately unopinionated about layout so it can drop into any
 * legal-agreement screen (mobile or therapist portal).
 */
export interface SignaturePadProps {
  onChange?: (dataUrl: string | null) => void;
  width?: number;
  height?: number;
  strokeColor?: string;
  strokeWidth?: number;
  disabled?: boolean;
  ariaLabel?: string;
}

export function SignaturePad({
  onChange,
  width = 480,
  height = 160,
  strokeColor = '#21272a',
  strokeWidth = 2.2,
  disabled = false,
  ariaLabel = 'Signature area',
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  // High-DPI: back the canvas with a pixel-doubled bitmap on retina screens.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth;
    }
  }, [width, height, strokeColor, strokeWidth]);

  const pointFrom = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const p = pointFrom(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    setIsDrawing(true);
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const p = pointFrom(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    if (canvasRef.current && onChange) {
      onChange(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange?.(null);
  }, [onChange]);

  return (
    <div>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={ariaLabel}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
        style={{
          display: 'block',
          border: '1px solid var(--ah-color-border)',
          borderRadius: 'var(--ah-radius-md)',
          background: 'var(--ah-color-surface)',
          touchAction: 'none',
          cursor: disabled ? 'not-allowed' : 'crosshair',
        }}
      />
      <div style={{ marginTop: 'var(--ah-space-2)', display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: 'var(--ah-color-text-muted)' }}>
          {hasInk ? 'Signed' : 'Sign in the box'}
        </span>
        <button
          type="button"
          onClick={clear}
          disabled={disabled || !hasInk}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--ah-color-primary)',
            cursor: hasInk ? 'pointer' : 'not-allowed',
            fontSize: 13,
          }}
        >
          Clear
        </button>
      </div>
    </div>
  );
}
